import { useCallback, useEffect, useRef, useState } from 'react';

import {
  WINDOWING_MIN_ROWS,
  buildRowGeometry,
  clampRowWindow,
  computeVariableRowWindow,
  type RowWindow,
  type WindowedRowPlan,
} from '@/lib/rowWindow';

const NO_PINS: number[] = [];

/** Fold the rendered `<tr>`s back into the height cache. A row carrying
 *  `data-window-index` starts an entry; attribute-less rows that follow
 *  (inline forms, error banners) extend it, so an entry's cached height
 *  is everything it contributes to the table. Spacers reset the fold. */
function measureRenderedRows(el: HTMLElement, heights: Array<number | undefined>) {
  let index = -1;
  let h = 0;
  const flush = () => {
    if (index >= 0 && h > 8) heights[index] = h;
  };
  for (const tr of el.querySelectorAll('tbody > tr')) {
    if (tr.hasAttribute('data-window-spacer')) {
      flush();
      index = -1;
      h = 0;
      continue;
    }
    const attr = tr.getAttribute('data-window-index');
    if (attr !== null) {
      flush();
      index = Number(attr);
      h = tr.getBoundingClientRect().height;
    } else if (index >= 0) {
      h += tr.getBoundingClientRect().height;
    }
  }
  flush();
}

function sameIndexList(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function samePlan(a: WindowedRowPlan, b: WindowedRowPlan): boolean {
  return (
    a.start === b.start &&
    a.end === b.end &&
    a.topPad === b.topPad &&
    a.bottomPad === b.bottomPad &&
    sameIndexList(a.pinnedBefore, b.pinnedBefore) &&
    sameIndexList(a.pinnedAfter, b.pinnedAfter)
  );
}

/** Window a long table against document (window) scroll so the DOM holds
 *  only the visible rows plus overscan (INP-778).
 *
 *  Attach `setContainer` to the element wrapping the `<table>`; give each
 *  entry's primary `<tr>` a `data-window-index` attribute and render
 *  `rows.slice(rowWindow.start, rowWindow.end)` between two spacer
 *  `<tr data-window-spacer>` rows of `topPad` / `bottomPad` px. Rendered
 *  entries are measured into a per-index cache (secondary rows — inline
 *  forms, error banners — fold into their entry), so variable heights
 *  produce exact spacers; unmeasured rows use the average of measured
 *  ones. A ResizeObserver on the container re-derives when content
 *  changes height without a scroll (a form expanding, notes wrapping).
 *
 *  `pinned` rows (open inline forms) never leave the DOM: outside the
 *  window they come back as `pinnedBefore` / `pinnedAfter` — render them
 *  before/after the spacers (pads already discount their heights) so the
 *  component state survives while total height stays exact.
 *
 *  Inactive (renders everything) below `WINDOWING_MIN_ROWS` — small runs
 *  keep Ctrl-F and zero scroll bookkeeping. The math lives in
 *  `lib/rowWindow.ts` (pure, vitest-covered); this hook only wires
 *  scroll/resize listeners and measurement.
 */
export function useWindowedRows(
  rowCount: number,
  {
    estimatedRowHeight = 44,
    overscan = 10,
    pinned,
  }: {
    estimatedRowHeight?: number;
    overscan?: number;
    /** Row indices that must stay mounted even when scrolled out. */
    pinned?: ReadonlySet<number>;
  } = {},
): {
  setContainer: (el: HTMLElement | null) => void;
  rowWindow: RowWindow;
  /** Pinned indices outside the window — render before the top spacer. */
  pinnedBefore: number[];
  /** Pinned indices outside the window — render after the bottom spacer. */
  pinnedAfter: number[];
  windowed: boolean;
} {
  const containerRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  // Measured entry heights by row index. Sparse; index-keyed, so a
  // sort/filter remap leaves stale values that self-correct on the next
  // measurement pass — they only ever feed spacer estimates.
  const heightsRef = useRef<Array<number | undefined>>([]);
  const windowed = rowCount >= WINDOWING_MIN_ROWS;
  if (heightsRef.current.length > rowCount) heightsRef.current.length = rowCount;

  const [plan, setPlan] = useState<WindowedRowPlan>(() =>
    computeVariableRowWindow({
      scrollTop: 0,
      viewportHeight: typeof window === 'undefined' ? 800 : window.innerHeight,
      listTop: 0,
      rowCount,
      overscan,
      geometry: buildRowGeometry(rowCount, [], estimatedRowHeight),
    }),
  );

  const recompute = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    measureRenderedRows(el, heightsRef.current);
    const geometry = buildRowGeometry(rowCount, heightsRef.current, estimatedRowHeight);
    const rect = el.getBoundingClientRect();
    const next = computeVariableRowWindow({
      scrollTop: window.scrollY,
      viewportHeight: window.innerHeight,
      listTop: rect.top + window.scrollY,
      rowCount,
      overscan,
      geometry,
      pinned,
    });
    setPlan((prev) => (samePlan(prev, next) ? prev : next));
  }, [rowCount, overscan, estimatedRowHeight, pinned]);

  useEffect(() => {
    if (!windowed) return;
    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        recompute();
      });
    };
    recompute();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // Catch height changes that happen without a scroll (an inline form
    // expanding, wrapped text reflowing after a font load).
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observerRef.current = observer;
    if (observer && containerRef.current) observer.observe(containerRef.current);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
      observerRef.current = null;
    };
  }, [windowed, recompute]);

  // Measure the freshly committed slice — a plan change renders rows
  // that may not be in the cache yet. Converges: an unchanged plan
  // bails at the identity check and stops the loop.
  useEffect(() => {
    if (windowed) recompute();
  }, [windowed, recompute, plan.start, plan.end]);

  const setContainer = useCallback((el: HTMLElement | null) => {
    const observer = observerRef.current;
    if (observer && containerRef.current) observer.unobserve(containerRef.current);
    containerRef.current = el;
    if (observer && el) observer.observe(el);
  }, []);

  if (!windowed) {
    return {
      setContainer,
      rowWindow: { start: 0, end: rowCount, topPad: 0, bottomPad: 0 },
      pinnedBefore: NO_PINS,
      pinnedAfter: NO_PINS,
      windowed,
    };
  }
  return {
    setContainer,
    rowWindow: clampRowWindow(plan, rowCount, estimatedRowHeight),
    pinnedBefore: plan.pinnedBefore.filter((i) => i < rowCount),
    pinnedAfter: plan.pinnedAfter.filter((i) => i < rowCount),
    windowed,
  };
}
